/**
 * Second-level rail navigation, per module.
 *
 * DERIVED from `shared/modules/catalog.json` (`shared/modules/registries.ts`):
 * each module's sections are its catalog groups, each row a page that is not
 * `navHidden`. Change a page there, never here.
 *
 * The hierarchy still mirrors the canonical PHP registries of the live
 * modules (coportal/config.php `PAGES`, lemax/registry.php, restools/config.php,
 * contracting/config.php, admin/registry.php, preferences/registry.php, and
 * includes/page-registry.php for the light modules), transcribed with PHP's
 * own rules: `nav_hidden` and `moved` pages are not rows, `admin_only` pages
 * are (RBAC is the server's), labels are PHP's where it has one. My Notes and
 * Hot Plate left every rail for the top bar (2026-07-29; see below), and the
 * light modules IT, Finance and Ecommerce have no rows left: the shell
 * mounts their identity plate on `activeModule && !onHome`, not on "has nav
 * rows".
 */
import type { ModuleSlug } from '@/styles/tokens';
import { DEFAULT_MODULE_TREE } from '@/shared/modules/catalog';
import { currentRegistries, hostOfPath, treeModule } from '@/shared/modules/store';
import { isCustomModule } from '@/shared/modules/custom';
import {
  deriveHotPlateModules,
  deriveModuleNav,
  deriveOffRailLabels,
  type NavGroup,
  type NavItem,
} from '@/shared/modules/registries';
import { MODULE_TILE } from './modules';

export type { NavGroup, NavItem };

/**
 * Every `ModuleSlug` is a key (a module with no rows maps to `[]`), keyed in
 * the catalog's `searchOrder`: the ⌘K index walks this object and its order
 * breaks ties between rows with the same label.
 */
export const MODULE_NAV: Record<ModuleSlug, NavGroup[]> = deriveModuleNav(DEFAULT_MODULE_TREE);

/**
 * Routes that render a real, named page but are deliberately ABSENT from
 * the rail (catalog pages with `offRail`): the region-filtered logs views,
 * reached from the logs toolbar's region pills, and the pages PHP marks
 * nav_hidden / moved. The Topbar breadcrumb resolves its page name through
 * `MODULE_NAV`, so without this map `/coportal/log-my` rendered the crumb as
 * "CS/OPS Portal" and nothing else (the page's own h1 left on 2026-08-10).
 *
 * Labels are the page's `crumb` (the title its route's wrapper passes) or,
 * where PHP has the slug, PHP's own label.
 */
export const OFF_RAIL_PAGE_LABELS: Record<string, string> = deriveOffRailLabels(DEFAULT_MODULE_TREE);

/**
 * Return the longest matching sub-nav `to` for a path. Used by the rail
 * to highlight the right sub-row when a route descends past the slug
 * (e.g. `/hr/cash-claims/123` → `/hr/cash-claims`). Falls back to `null`
 * when nothing matches.
 */
export function resolveActiveSubNav(
  modSlug: ModuleSlug,
  pathname: string,
): string | null {
  // The tenant's rail: a switched-off page is no row to light up.
  const groups = currentRegistries().nav[modSlug];
  if (!groups) return null;
  let best: string | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      // Exact match, or `pathname` starts with `${to}/` so deeper
      // routes still light up the parent row.
      if (pathname === item.to || pathname.startsWith(item.to + '/')) {
        if (best === null || item.to.length > best.length) {
          best = item.to;
        }
      }
    }
  }
  return best;
}

/**
 * Recognised module slug for a given pathname (first path segment), or
 * `null` for `/dashboard`, `/login`, `/forbidden`. The Rail uses this to
 * decide whether to render the second-level nav block at all.
 */
const KNOWN_SLUGS = new Set<string>(Object.keys(MODULE_NAV));

export function resolveModuleFromPath(pathname: string): ModuleSlug | null {
  // A row another module borrows (Guest Portal ▸ Content, while it is on)
  // opens in the borrowing module: its rail, its ink, its crumb.
  const borrowed = currentRegistries().borrowed;
  for (const [to, mod] of Object.entries(borrowed)) {
    if (pathname === to || pathname.startsWith(`${to}/`)) return mod;
  }
  // Strip leading slash, take first segment.
  const segs = pathname.replace(/^\/+/, '').split('/');
  const seg = segs[0] ?? '';
  // A team module (Admin ▸ Modules) or a custom module (a spec, brief §5.4):
  // `/m/<id>/…`, one address space.
  if (seg === 'm') {
    const id = segs[1] ?? '';
    const m = id ? treeModule(id) : undefined;
    return m && (m.team || isCustomModule(m)) ? (id as ModuleSlug) : null;
  }
  // A template placed inside another module answers to that module's rail.
  const host = hostOfPath(pathname);
  if (host) return host as ModuleSlug;
  return KNOWN_SLUGS.has(seg) ? (seg as ModuleSlug) : null;
}

/** Narrowing guard for anything read back from storage / a query string. */
export function isModuleSlug(value: string | null | undefined): value is ModuleSlug {
  return typeof value === 'string' && KNOWN_SLUGS.has(value);
}

// ── Personal surfaces hoisted to the top bar (2026-07-29) ──────────────
//
// My Notes and Hot Plate used to appear in EVERY module's rail — 25 entries
// in total (13 My Notes + 12 Hot Plate). They behave very differently
// though, and the top bar treats them differently as a result:
//
//   My Notes  is genuinely global. Every module's page is a thin wrapper
//             around the same <ModuleMyNotesPage> hitting /api/notes, which
//             is user-scoped. One canonical route serves all of them.
//
//   Hot Plate is NOT global. Each module renders
//             <HotPlateBoard mod="<slug>" scope="team" /> — a team board
//             scoped to that module. Collapsing them into one route would
//             merge every team's cards back together, which is exactly the
//             bug docs/migrations/2026-05-25_hot-plate-mod-scope.sql closes.
//             So the top-bar button is MODULE-AWARE: it opens the board for
//             wherever you currently are.

/** Canonical home for the per-user notes surface. */
export const MY_NOTES_PATH = '/my-notes';

/**
 * Modules that own a `<slug>/hot-plate` team board.
 *
 * The catalog modules with `hotPlate` (not derived from the rail: the rows it
 * would have been derived FROM are the ones the hoist removed).
 * `moduleNav.test.ts` asserts every slug here has a real route, so an
 * out-of-sync catalog fails the suite rather than 404ing a user.
 *
 * NOTE aiaiai is intentionally absent — its board is the *personal* Workflow
 * Kanban at /aiaiai/workflow-kanban (scope="personal"), not a team hot plate.
 * admin and preferences never had one.
 *
 * NOTE `data` is absent too (removed 2026-07-31). It was listed here, but
 * `features/data/data.routes.tsx` has no `hot-plate` route — its catch-all
 * `<Navigate to="." replace />` bounced you straight back to /data, so the
 * top-bar button read as broken throughout the Data module. Data owns the
 * Tariff catalog, not a team board; if one is ever ported, add the route AND
 * the slug back together (`moduleNav.test.ts` now asserts the route exists).
 */
const HOT_PLATE_MODULES: readonly ModuleSlug[] = deriveHotPlateModules(DEFAULT_MODULE_TREE);

const HOT_PLATE_SET: ReadonlySet<string> = new Set(HOT_PLATE_MODULES);

export { HOT_PLATE_MODULES };

/**
 * Tile a module's hot plate is gated behind — its canonical PHP tile slug.
 *
 * HR's rail entry carried `requiresTile: 'hr'`; hoisting it to the top bar
 * would have silently dropped that gate and shown every user a button that
 * 403s. HR was special-cased at first, which left the OTHER gap open: the
 * button also renders for the *remembered* module on module-less pages
 * (/my-notes, the hub home), so a cold deep link to /my-notes offered a
 * CS/OPS Portal board to someone who may not hold the `cs-portal` tile.
 * Gating on the module's own tile closes both cases at once — `canSeeTile`
 * already honours per-page grants, so a user granted `it:hot-plate` but no
 * `it` tool still sees the button. The route itself is protected either
 * way; this only keeps the button from appearing when it can't be used.
 */
export function hotPlateRequiredTile(mod: ModuleSlug): string {
  return MODULE_TILE[mod];
}

/**
 * Hot-plate path for the module you are currently in, or null when the
 * current module has no team board (or you are on a non-module page like
 * /dashboard). The top bar hides the button when this is null rather than
 * linking somewhere arbitrary.
 */
export function resolveHotPlatePath(pathname: string): string | null {
  const mod = resolveModuleFromPath(pathname);
  if (!mod) return null;
  return hotPlatePathForModule(mod);
}

/**
 * Hot Plate route for an explicit module, independent of the current URL.
 *
 * Needed because two destinations are module-LESS — the global
 * `/my-notes` and the hub home — so resolving from the pathname alone
 * yields null there and the Hot Plate button silently disappears. My
 * Notes is precisely where someone is most likely to want to jump to
 * their board, so the Topbar passes the last module it saw instead.
 */
export function hotPlatePathForModule(mod: ModuleSlug): string | null {
  return HOT_PLATE_SET.has(mod) ? `/${mod}/hot-plate` : null;
}

/** Fallback module when nothing has been visited yet this session ·
 *  matches ModuleMyNotesPage's own default. */
export const DEFAULT_HOT_PLATE_MODULE: ModuleSlug = 'coportal';

/**
 * sessionStorage key holding the last module actually visited.
 *
 * The Topbar writes it (so the Hot Plate button survives a reload on the
 * module-less `/my-notes`), the ⌘K launcher reads it, and `AuthContext`
 * CLEARS it on sign-in and sign-out. Without that clear, a second user
 * signing in on the same tab inherits the first user's last module: the
 * button is still tile-gated so they cannot reach a board they lack, but the
 * fact that the previous session ended in HR should not leak into theirs.
 * Exported from here rather than from a component so all three can agree on
 * one string.
 */
export const LAST_MODULE_KEY = 'amassa.shell.lastModule';

/** Forget the tab-local shell memory. Called on every auth transition. */
export function clearShellSessionMemory(): void {
  try {
    window.sessionStorage.removeItem(LAST_MODULE_KEY);
  } catch {
    /* private mode / storage disabled — nothing was stored anyway */
  }
}
